import { BeakerIcon } from "@heroicons/react/outline";
import { RocketIcon } from "@radix-ui/react-icons";
import classNames from "classnames";
import React from "react";

import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";

export type LabelBadgeVariant = "default" | "error" | "environment";
export type LabelBadgeSize = "default" | "mini";

interface LabelAppearanceConfig {
  names: string[];
  dotClassName: string;
  badgeClassName: string;
  renderFlair: () => React.ReactNode;
}

interface LabelBadgeProps {
  label: string;
  variant?: LabelBadgeVariant;
  size?: LabelBadgeSize;
  className?: string;
  showTooltip?: boolean;
}

const LABEL_APPEARANCE_CONFIG: LabelAppearanceConfig[] = [
  {
    names: ["prod", "production"],
    dotClassName: "bg-blue-600",
    badgeClassName: "border-blue-800 bg-blue-700 text-white",
    renderFlair: () => <RocketIcon className="inline-block h-3 w-3 align-middle" />,
  },
  {
    names: ["staging", "dev", "development"],
    dotClassName: "bg-blue-600",
    badgeClassName: "border-blue-800 bg-blue-700 text-white",
    renderFlair: () => <BeakerIcon className="inline-block h-3 w-3 align-middle" />,
  },
];

const getLabelAppearanceConfig = (name: string) =>
  LABEL_APPEARANCE_CONFIG.find(config => config.names.includes(name.toLowerCase()));

export const getEnvFlair = (name: string) => getLabelAppearanceConfig(name)?.renderFlair() ?? null;

export const getLabelDotClassName = (label: string, variant: LabelBadgeVariant = "default") => {
  if (variant === "error") {
    return "bg-red-500";
  }

  if (variant === "environment") {
    return getLabelAppearanceConfig(label)?.dotClassName ?? "bg-gray-500";
  }

  return "bg-gray-500";
};

const LabelBadge: React.FC<LabelBadgeProps> = ({
  label,
  variant = "default",
  size = "default",
  className,
  showTooltip = true,
}) => {
  const labelAppearanceConfig = getLabelAppearanceConfig(label);
  const envFlair =
    variant === "environment" || variant === "error" ? labelAppearanceConfig?.renderFlair() : null;
  const hasEnvFlair = !!envFlair;
  const isMini = size === "mini";

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-sm border font-medium",
        isMini
          ? "max-w-[120px] px-1 py-px text-[10px] leading-tight"
          : "max-w-[200px] px-2 py-0.5 text-xs",
        {
          [labelAppearanceConfig?.badgeClassName ?? ""]: hasEnvFlair && variant === "environment",
          "border-gray-400 bg-white text-black": !hasEnvFlair && variant === "default",
          "border-red-300 bg-red-50 text-red-700": variant === "error",
          "border-gray-400 bg-gray-100 text-black": !hasEnvFlair && variant === "environment",
        },
        className,
      )}
    >
      {envFlair && (
        <span className={classNames("flex flex-shrink-0 items-center", isMini ? "mr-0.5" : "mr-1")}>
          {envFlair}
        </span>
      )}
      {showTooltip ? (
        <OverflowDisplayTooltip content={label} className="min-w-0">
          <span className="truncate">{label}</span>
        </OverflowDisplayTooltip>
      ) : (
        <span className="truncate">{label}</span>
      )}
    </span>
  );
};

export default LabelBadge;
